/* eslint-disable react/no-array-index-key */
import * as React from 'react';
import { Box, Button, CircularProgress, useTheme } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { IUniformProps, IUniformStepProps, UniformStepType, ExtraButtons } from './interfaces';
import { UniformStepper } from './uniform-stepper';
import { UniformLayout } from './uniform-layout';

const defaultLabels = {
  previousLabel: 'Forrige',
  nextLabel: 'Næste',
  completeLabel: 'Udfør',
  cancelLabel: 'Afbryd',
};

export const Uniform = forwardRef((props: IUniformProps, ref) => {
  const {
    step,
    onStepChange,
    children,
    onOk,
    isEditMode,
    showAll,
    forceHideStepper,
    launchbox,
    sidebar,
    onCancel,
    titleZone,
    showSpinnerOnNextAndComplete,
    showSpinnerOnPrevious,
    labels = defaultLabels,
    showButtons = true,
    useLoaderButtonAsOk = false,
    okButtonIsLoading = false,
    disableCloseOnEscapeKey,
    allowStepNavigation = false,
  } = props;
  const { previousLabel, nextLabel, completeLabel, cancelLabel } = { ...defaultLabels, ...labels };
  const theme = useTheme();
  const [internalStep, setInternalStep] = useState(step || 0);
  useImperativeHandle(ref, () => ({
    setStep(stepNumber: number) {
      setStep(stepNumber);
    },
  }));

  useEffect(() => {
    setInternalStep(step || 0);
    if (!disableCloseOnEscapeKey) {
      window.addEventListener('keydown', e => {
        if (e.code === 'Escape') {
          window.close();
        }
      });
    }
  }, [step]);
  const stepsArray = (Array.isArray(children) ? children : [children]).filter(iStep => {
    if (iStep.props && iStep.props.visible === false) return false;
    return true;
  });

  const currentStepProps = stepsArray[internalStep].props as IUniformStepProps;

  async function handleStepChange(stepNumber: number) {
    if (stepNumber >= 0 && stepNumber < stepsArray.length) {
      if (stepNumber > internalStep && currentStepProps.validate && !currentStepProps.validate()) {
        return;
      }
      const stepChangeResult = onStepChange ? await onStepChange(stepNumber) : undefined;
      if (stepChangeResult === false) {
        return;
      }
      setInternalStep(stepNumber);
    }
  }
  async function handleOnOk() {
    if (currentStepProps.validate && !currentStepProps.validate()) {
      return;
    }
    onOk && (await onOk(internalStep));
    window.opener && window.opener.postMessage({ event: 'uniformOkClicked' });
  }
  function setStep(stepNumber: number) {
    setInternalStep(stepNumber);
  }
  const currentStepContent = <div>{stepsArray.map((x: UniformStepType, i: number) => (i === internalStep ? <span key={`${i}Step`}>{x}</span> : <span key={`${i}Step`} />))}</div>;
  return (
    <UniformLayout isEditMode={isEditMode} launchbox={launchbox} titleZone={titleZone}>
      {!showAll && (
        <Box flex="1" display="flex" flexDirection="column">
          {!forceHideStepper && stepsArray && stepsArray.length > 1 && (
            <UniformStepper activeStep={internalStep} steps={stepsArray} onStepClick={allowStepNavigation ? handleStepChange : undefined} />
          )}
          {sidebar ? (
            // Three columns: the content keeps the centred width it has without a sidebar, the sidebar floats in
            // the middle of the space left of it, and the empty right column is the left one's counterweight. The
            // content grows first, so the sides only share what is left over; squeezed, the right column gives
            // way before the content does, and the content before the sidebar.
            <Box display="flex" flex="1">
              <Box flex="1 1 0" minWidth="fit-content" display="flex" justifyContent="center" alignItems="flex-start">
                {sidebar}
              </Box>
              <Box flex="1000 1 0" maxWidth={theme.breakpoints.values.lg} minWidth={0}>
                {currentStepContent}
              </Box>
              <Box flex="1 1 0" minWidth={0} />
            </Box>
          ) : (
            currentStepContent
          )}
        </Box>
      )}
      {showAll && (
        <div>
          {stepsArray.map((x: UniformStepType, i: number) => (
            <span key={`${i}Step`}>{x}</span>
          ))}
        </div>
      )}
      {showButtons && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            background: 'white',
            py: 1,
            px: 2,
            alignItems: 'center',
          }}
        >
          <Button sx={{ marginRight: 'auto' }} variant="outlined" onClick={() => onCancel && onCancel(0)}>
            {cancelLabel}
          </Button>

          {internalStep !== 0 && (
            <Button
              sx={{ marginLeft: '5px' }}
              variant="outlined"
              disabled={internalStep === 0 || currentStepProps.disableButtons?.includes('prev')}
              onClick={() => handleStepChange(internalStep - 1)}
            >
              {showSpinnerOnPrevious ? <CircularProgress sx={{ color: '#fff' }} size={25} /> : previousLabel}
            </Button>
          )}

          {currentStepProps?.extraButtons &&
            currentStepProps?.extraButtons.map((p: ExtraButtons, index: number) => (
              <LoadingButton
                sx={{ marginLeft: '5px' }}
                variant="outlined"
                key={`${index}ExtraButtons`}
                onClick={p.onClick}
                startIcon={p.icon || null}
                loading={p.isLoading || false}
                disabled={p.disabled}
              >
                {p.title}
              </LoadingButton>
            ))}

          {internalStep !== stepsArray.length - 1 && (
            <Button sx={{ marginLeft: '5px' }} variant="contained" disabled={currentStepProps.disableButtons?.includes('next')} onClick={() => handleStepChange(internalStep + 1)}>
              {showSpinnerOnNextAndComplete ? <CircularProgress sx={{ color: '#fff' }} size={25} /> : nextLabel}
            </Button>
          )}
          {internalStep === stepsArray.length - 1 &&
            (useLoaderButtonAsOk ? (
              <LoadingButton
                sx={{ marginLeft: '5px' }}
                variant="contained"
                disabled={currentStepProps.disableButtons?.includes('next') || okButtonIsLoading}
                onClick={handleOnOk}
                loading={okButtonIsLoading}
              >
                {completeLabel}
              </LoadingButton>
            ) : (
              <Button sx={{ marginLeft: '5px' }} variant="contained" disabled={currentStepProps.disableButtons?.includes('next')} onClick={handleOnOk}>
                {showSpinnerOnNextAndComplete ? <CircularProgress sx={{ color: '#fff' }} size={25} /> : completeLabel}
              </Button>
            ))}
        </Box>
      )}
    </UniformLayout>
  );
});
